import { Button } from '@HoudiniUI/Button';
import { Input } from '@HoudiniUI/Input';
import { THEME } from '@Theme';
import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { IAICampaignRecipient } from '../../../models/AICampaign';
import { createContentStateWithDocument } from '../../../models/utils/richContentUtils';
import {
	invalidateAICampaignDetails,
	invalidateAICampaignRecipients,
	useAICampaignDetails,
	useAICampaignRecipientEmail,
	useAICampaignRecipients,
	useRemoveCampaignRecipient,
	useStartAICampaignGeneration,
	useUpdateCampaignRecipient,
} from '../../../queries/AICampaign';
import { AICampaignGenerationProgress } from '../templates/AICampaignGenerationProgress';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { RichContentDocumentEditor } from '../../components/richContent/RichContentDocumentEditor';
import { baseStyleSheet } from '../../styles/styles';

/** Rows rendered before the list waits for a scroll; a campaign can have thousands of recipients. */
const RecipientPageSize = 50;

interface IProps {
	campaignId: string;
	onCancelCampaign?(e?: React.MouseEvent<HTMLElement>): void;
	onChangeSchedule?(): void;
}

/**
 * The queued view of an AI campaign. An ordinary campaign shows its one composed email here, but an AI campaign's
 * emails are written per recipient, so the left pane previews whichever recipient is selected on the right, starting
 * with the first, and editing or removing applies to the email being read. Before generation there is nothing to
 * preview yet; the panes explain what will happen, offer to write the emails now, and follow the run's progress
 * until the recipients take their place.
 */
export const AICampaignQueuedView: React.FC<IProps> = ({ campaignId, onCancelCampaign, onChangeSchedule }) => {
	// Fetched here rather than taken from the caller's view model so that generation finishing (which invalidates
	// these details) flips this screen to the recipient list without anyone reloading the modal.
	const { data: details } = useAICampaignDetails({ bulkEmailMessageId: campaignId });
	const isGenerated = !!details?.aiBrief?.lastGeneratedDate;
	const campaignName = details?.name;
	const scheduleStartDate = details?.schedule?.startDate;

	const [startedJobId, setStartedJobId] = useState<string | null>(null);
	const generateMutation = useStartAICampaignGeneration({
		onError: error => toast.error(error?.systemMessage || 'Could not start writing these emails'),
		onSuccess: job => setStartedJobId(job.jobId),
	});
	const generationJobId = startedJobId ?? details?.aiBrief?.generationJobId;

	const { data: recipients, isLoading: recipientsLoading } = useAICampaignRecipients({
		bulkEmailMessageId: campaignId,
		enabled: isGenerated,
	});

	const rows = useMemo(() => recipients ?? [], [recipients]);
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
	const [visibleCount, setVisibleCount] = useState(RecipientPageSize);
	const [isEditing, setIsEditing] = useState(false);
	const [draftSubject, setDraftSubject] = useState('');
	const [draftContent, setDraftContent] = useState<Api.IRichContentEditorState | null>(null);

	useEffect(() => {
		if (!selectedContactId && rows.length > 0) {
			setSelectedContactId(rows[0].contactId);
		}
	}, [rows, selectedContactId]);

	const selected = rows.find(r => r.contactId === selectedContactId) ?? null;
	const {
		data: email,
		isLoading: emailLoading,
		isSuccess: emailLoaded,
	} = useAICampaignRecipientEmail({
		bulkEmailMessageId: campaignId,
		contactId: selectedContactId ?? '',
		enabled: isGenerated && !!selectedContactId,
	});

	const readOnlyContent = useMemo(() => createContentStateWithDocument(email?.htmlContent ?? ''), [email?.htmlContent]);

	const updateMutation = useUpdateCampaignRecipient({
		onError: error => toast.error(error?.systemMessage || 'Could not save this email'),
		onSuccess: () => {
			toast.success('Email updated');
			setIsEditing(false);
		},
	});

	const removedNameRef = useRef('Recipient');
	const removeMutation = useRemoveCampaignRecipient({
		onError: error => toast.error(error?.systemMessage || 'Could not remove this recipient'),
		onSuccess: () => toast.success(`${removedNameRef.current} removed from this campaign`),
	});

	const removeSelected = () => {
		if (!selected) {
			return;
		}

		const index = rows.findIndex(r => r.contactId === selected.contactId);
		removedNameRef.current = selected.name || 'Recipient';
		// Selection moves off the recipient before the request: removal drops their cached email and refreshes the
		// list, and a still-selected recipient would refetch an email the API can only 404 on.
		setSelectedContactId(rows[index + 1]?.contactId ?? rows[index - 1]?.contactId ?? null);
		removeMutation.mutate({ bulkEmailMessageId: campaignId, contactId: selected.contactId });
	};

	const selectRecipient = (contactId: string) => {
		setIsEditing(false);
		setSelectedContactId(contactId);
	};

	// Only editable once the body has actually loaded. Opening the editor on a failed fetch would show an empty
	// draft, and saving it would overwrite the recipient's real email with nothing.
	const startEditing = () => {
		if (!emailLoaded) {
			return;
		}

		setDraftSubject(email?.subject ?? selected?.subject ?? '');
		setDraftContent(createContentStateWithDocument(email?.htmlContent ?? ''));
		setIsEditing(true);
	};

	const save = () => {
		if (!draftContent || !selectedContactId) {
			return;
		}

		updateMutation.mutate({
			bulkEmailMessageId: campaignId,
			contactId: selectedContactId,
			subject: draftSubject,
			content: draftContent.getRawRichTextContent(),
		});
	};

	const renderPreview = () => {
		if (!isGenerated) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<h3 className={css(styleSheet.emptyHeadline)}>The emails have not been written yet</h3>
					<p className={css(styleSheet.emptyBody)}>
						Each recipient gets their own email, written about a day and a half before the send so the research is
						current. They will appear here as soon as they are ready, and you will be able to read, edit, or remove any
						of them before they go out.
					</p>
				</div>
			);
		}

		if (recipientsLoading || (emailLoading && !email)) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<LoadingSpinner type='large' />
				</div>
			);
		}

		if (!selected) {
			return (
				<div className={css(styleSheet.emptyState)}>
					<h3 className={css(styleSheet.emptyHeadline)}>No recipients</h3>
					<p className={css(styleSheet.emptyBody)}>Every recipient has been removed from this campaign.</p>
				</div>
			);
		}

		if (isEditing && !!draftContent) {
			return (
				<>
					<Input
						onChange={event => setDraftSubject(event.target.value)}
						placeholder='Subject'
						styles={[styleSheet.editorSubject]}
						value={draftSubject}
					/>
					<RichContentDocumentEditor
						contentState={draftContent}
						onContentStateChanged={setDraftContent}
						styles={[styleSheet.editableEditor]}
					/>
					<div className={css(styleSheet.editActions)}>
						<Button disabled={updateMutation.isPending} onClick={() => setIsEditing(false)} variant='outline'>
							Cancel
						</Button>
						<Button
							disabled={!draftSubject.trim() || updateMutation.isPending}
							isLoading={updateMutation.isPending}
							onClick={save}
							variant='primary'
						>
							Save
						</Button>
					</div>
				</>
			);
		}

		return (
			<>
				<div className={css(styleSheet.previewHeader)}>
					<div className={css(styleSheet.previewMeta)}>
						<span className={css(styleSheet.previewRecipient)}>
							To: {selected.name || selected.emailAddress || selected.contactId}
						</span>
						<span className={css(styleSheet.previewSubject)}>Subject: {email?.subject ?? selected.subject}</span>
					</div>
					<div className={css(styleSheet.previewActions)}>
						<Button
							disabled={rows.length <= 1 || removeMutation.isPending}
							isLoading={removeMutation.isPending}
							onClick={removeSelected}
							variant='destructive-secondary'
						>
							Remove recipient
						</Button>
						<Button disabled={!emailLoaded} onClick={startEditing} variant='outline'>
							Edit email
						</Button>
					</div>
				</div>
				<RichContentDocumentEditor readOnly contentState={readOnlyContent} styles={[styleSheet.previewBody]} />
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.previewPane)}>
				{!!campaignName && <h2 className={css(styleSheet.campaignName)}>{campaignName}</h2>}
				{renderPreview()}
			</div>

			<div className={css(styleSheet.rail)}>
				{!!scheduleStartDate && (
					<div className={css(styleSheet.scheduleCard)}>
						<span className={css(styleSheet.scheduleTitle)}>Send Time</span>
						<time dateTime={scheduleStartDate}>
							{moment(new Date(scheduleStartDate)).format('dddd, MMMM Do, YYYY, h:mm A')}
						</time>
						<div className={css(styleSheet.scheduleActions)}>
							{!!onChangeSchedule && (
								<button className={css(baseStyleSheet.brandLink)} onClick={onChangeSchedule} type='button'>
									<span>Change</span>
								</button>
							)}
							{!!onCancelCampaign && (
								<button className={css(styleSheet.cancelLink)} onClick={onCancelCampaign} type='button'>
									<span>Cancel Campaign</span>
								</button>
							)}
						</div>
					</div>
				)}

				{isGenerated ? (
					<div className={css(styleSheet.recipientsCard)}>
						<span className={css(styleSheet.recipientsTitle)}>
							{`${rows.length.toLocaleString()} ${rows.length === 1 ? 'recipient' : 'recipients'}`}
						</span>
						<div className={css(styleSheet.recipientsList)}>
							{rows.slice(0, visibleCount).map(recipient => (
								<RecipientRow
									isSelected={recipient.contactId === selectedContactId}
									key={recipient.contactId}
									onClick={() => selectRecipient(recipient.contactId)}
									recipient={recipient}
								/>
							))}
							{visibleCount < rows.length && (
								<Waypoint bottomOffset='-200px' onEnter={() => setVisibleCount(count => count + RecipientPageSize)} />
							)}
						</div>
					</div>
				) : (
					<div className={css(styleSheet.noticeCard)}>
						<p className={css(styleSheet.noticeHeadline)}>Every recipient will get their own email</p>
						<p className={css(styleSheet.noticeBody)}>
							The emails are written about a day and a half before the send. You will hear from us when they are ready
							to review.
						</p>
						{generationJobId ? (
							<AICampaignGenerationProgress
								jobId={generationJobId}
								onComplete={() => {
									invalidateAICampaignDetails(campaignId);
									invalidateAICampaignRecipients(campaignId);
								}}
							/>
						) : (
							<Button
								disabled={generateMutation.isPending}
								isLoading={generateMutation.isPending}
								onClick={() => generateMutation.mutate(campaignId)}
								variant='outline'
							>
								Write them now
							</Button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

const RecipientRow = ({
	isSelected,
	onClick,
	recipient,
}: {
	isSelected: boolean;
	onClick: () => void;
	recipient: IAICampaignRecipient;
}) => {
	// A raw button rather than a components-v2 Button: this is a full-width two-line selectable list row, which none
	// of the Button variants model. aria-pressed carries the selected state that the styling shows.
	return (
		<button
			aria-pressed={isSelected}
			className={css(styleSheet.recipientRow, isSelected ? styleSheet.recipientRowSelected : null)}
			onClick={onClick}
			type='button'
		>
			<span className={css(styleSheet.recipientName)}>
				{recipient.name || recipient.emailAddress || recipient.contactId}
			</span>
			<span className={css(styleSheet.recipientSubject)}>
				{recipient.subject || <em>No subject</em>}
				{!recipient.hasContent && <span className={css(styleSheet.recipientMissing)}> (no email written)</span>}
			</span>
		</button>
	);
};

const styleSheet = StyleSheet.create({
	campaignName: {
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	container: {
		boxSizing: 'border-box',
		display: 'flex',
		gap: THEME.spacing.lg,
		height: '100%',
		padding: THEME.spacing.md,
		width: '100%',
	},
	editActions: {
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'flex-end',
	},
	// TinyMCE is configured with height 100%, which resolves to auto against a parent without a definite height,
	// collapsing the edit area; the editable editor needs a real one.
	//
	// The border is ours to draw: RichContentDocumentEditor strips TinyMCE's own chrome globally
	// (`.rich-content-document-editor .tox.tox-tinymce { border: none }`), so an editor sitting on a white card has
	// no edge unless its wrapper supplies one. Without it the rounding below had nothing to round.
	editableEditor: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		flex: 1,
		minHeight: 420,
	},
	editorSubject: {
		width: '100%',
	},
	emptyBody: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		maxWidth: 480,
	},
	emptyHeadline: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	emptyState: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		justifyContent: 'center',
		textAlign: 'center',
	},
	cancelLink: {
		background: 'none',
		border: 'none',
		color: THEME.colors.danger.main,
		cursor: 'pointer',
		fontSize: THEME.typography.fontSizes.sm,
		padding: 0,
	},
	noticeBody: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
	},
	noticeCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		padding: THEME.spacing.md,
	},
	noticeHeadline: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	previewActions: {
		display: 'flex',
		flexShrink: 0,
		gap: THEME.spacing.sm,
	},
	previewBody: {
		background: THEME.colors.background.light,
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
	},
	previewHeader: {
		alignItems: 'flex-start',
		borderBottom: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		gap: THEME.spacing.md,
		justifyContent: 'space-between',
		paddingBottom: THEME.spacing.sm,
	},
	previewMeta: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minWidth: 0,
	},
	previewPane: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		minWidth: 0,
		overflow: 'hidden',
		padding: THEME.spacing.lg,
	},
	previewRecipient: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
	},
	previewSubject: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	rail: {
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		gap: THEME.spacing.md,
		height: '100%',
		minHeight: 0,
		width: 360,
	},
	recipientMissing: {
		color: THEME.colors.danger.main,
		fontSize: THEME.typography.fontSizes.xs,
	},
	recipientName: {
		fontWeight: THEME.typography.fontWeights.bold,
		...THEME.common.truncate,
	},
	recipientRow: {
		background: 'none',
		border: 'none',
		borderRadius: THEME.common.borderRadius,
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		padding: THEME.spacing.sm,
		textAlign: 'left',
		width: '100%',
		':hover': {
			background: THEME.colors.background.main,
		},
	},
	recipientRowSelected: {
		background: THEME.colors.background.main,
		boxShadow: `inset 3px 0 0 ${THEME.colors.primary.main}`,
	},
	recipientsCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		minHeight: 0,
		padding: THEME.spacing.md,
	},
	recipientsList: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minHeight: 0,
		overflowY: 'auto',
	},
	recipientsTitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	recipientSubject: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
		width: '100%',
		...THEME.common.truncate,
	},
	scheduleActions: {
		display: 'flex',
		gap: THEME.spacing.md,
	},
	scheduleCard: {
		background: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		gap: THEME.spacing.xs,
		padding: THEME.spacing.md,
	},
	scheduleTitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: THEME.typography.fontWeights.bold,
	},
});
